/**
 * TeacherHubDocPicker
 * ────────────────────
 * Single-select "tag a document from DeskeyHUB" picker, opened from the
 * Auto Sync upload popup's reference-material section. Modeled on the
 * student tutor chat's HubAttachModal, but single-select and backed by the
 * teacher-scoped GET /api/hub/teacher/docs/search (teachers browse every
 * department's library, so no department filter is needed).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { X, Search, FileText, Library } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeacherHubDocPicker({ onClose, onSelect }) {
  const [q, setQ] = useState('');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('desky_token') || '';
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      const res = await fetch(`${API}/api/hub/teacher/docs/search?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json().catch(() => ({}));
      setDocs(d.documents || []);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(''); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 280);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4" data-testid="teacher-hub-picker"
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)' }} onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }}>
            <Library className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-extrabold text-slate-900">Tag from DeskeyHUB</h3>
            <p className="text-[11px] text-slate-500">Pick a reference document — Auto Sync will match it against the lecture transcript.</p>
          </div>
          <button onClick={onClose} data-testid="teacher-hub-picker-close" className="w-8 h-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pt-4 pb-3 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by title, description or tags…"
              data-testid="teacher-hub-picker-search"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white text-sm focus:outline-none focus:border-emerald-400 transition-all"
            />
          </div>
        </div>

        <div className="px-6 pb-5 overflow-auto flex-1" data-testid="teacher-hub-picker-list">
          {loading ? (
            <div className="text-sm text-slate-500 py-8 text-center">Searching…</div>
          ) : docs.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No documents match your search.</div>
          ) : (
            <ul className="space-y-2">
              {docs.map(d => (
                <li key={d.id}>
                  <button
                    onClick={() => onSelect(d)}
                    data-testid={`teacher-hub-picker-doc-${d.id}`}
                    className="w-full text-left flex items-start gap-3 rounded-2xl p-3 border-2 border-zinc-100 hover:border-emerald-300 hover:bg-emerald-50/40 transition-all">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-zinc-100 text-slate-500">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900 truncate">{d.title}</div>
                      {d.description && <div className="text-[11px] text-slate-500 line-clamp-1">{d.description}</div>}
                      <div className="text-[10px] text-slate-400 mt-1 font-mono uppercase tracking-wider">
                        {d.file_ext || 'file'}{(d.tags || []).slice(0, 3).map(t => ` · ${t}`).join('')}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
